import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useState } from 'react';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { Link, useNavigate } from 'react-router-dom';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentInvoiceTask } from '@/providers/InvoiceTaskProvider';
import { useUpdateInvoiceTask } from '@/hooks/api/1820_update_invoice_task';
import { useInvoiceTaskInvalidate } from '@/hooks/api/1840_invoice_task';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import Flatpickr from 'react-flatpickr';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useSalesOrderTargetUsers } from '@/hooks/api/1231_sales_order_target_users';

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  id: number;
  name: string;
  description?: string;
  billing_schedule_detail_id: number;
  due_date?: string;
  user_id: number;
};

export const InvoiceTaskEditForm = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { invoiceTask: task } = useCurrentInvoiceTask();
  const { data: targetUsers } = useSalesOrderTargetUsers({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { mutate, data, isPending, error } = useUpdateInvoiceTask();
  const { invalidate } = useInvoiceTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: _so_!.id,
    id: task!.id,
  });
  console.log(task);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      id: task!.id,
      name: task!.name,
      description: task!.description,
      billing_schedule_detail_id: task!.billing_schedule_detail.id,
      due_date: task!.due_date,
      user_id: task!.user.id,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('請求タスクの編集が完了しました');
      navigate(-1);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (!targetUsers) return <></>;

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12 col-lg-9 mx-auto">
          <div className="card mb-4" id="basic-info">
            <div className="card-header d-flex align-items-center">
              <div className="col-md-8">
                <h5 className="mb-0">請求タスク編集</h5>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body pt-0 row">
                <ApiErrorMessages error={error} />

                <div className="form-group">
                  <label>プロジェクト名</label>
                  <p className="form-text text-muted text-sm ms-1">{_so_!.name}</p>
                </div>
                <FormGroup label="タスク名" required={true}>
                  <input
                    type="text"
                    maxLength={50}
                    className={`form-control ${errors.name && 'is-invalid'}`}
                    placeholder={'タスク名'}
                    {...register('name', {
                      required: '入力して下さい。',
                    })}
                  />
                  {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                </FormGroup>

                <FormGroup label="メモ">
                  <textarea className="form-control" maxLength={2000} rows={5} {...register('description')}></textarea>
                </FormGroup>

                <div className="col-12 col-md-6">
                  <FormGroup label="計上月" required={true}>
                    <input
                      type="text"
                      maxLength={50}
                      className={`form-control`}
                      defaultValue={task!.billing_schedule_detail.billing_schedule_outline.year_month}
                      disabled={true}
                    />
                  </FormGroup>
                </div>

                <div className="col-12 col-md-6">
                  <FormGroup label="期限日">
                    <Controller
                      control={control}
                      name="due_date"
                      render={({ field: { ref, ...field } }) => (
                        <Flatpickr
                          className={`form-control datepickr`}
                          {...field}
                          onChange={(_, dateString) => {
                            field.onChange(dateString);
                            field.onBlur();
                          }}
                          onBlur={() => {}}
                          options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                        />
                      )}
                    />
                  </FormGroup>
                </div>

                <div className="col-12 col-md-6">
                  <FormGroup label="タスク担当者" required={true}>
                    <select className="form-control" required={true} {...register('user_id')}>
                      {targetUsers.map((tu, i) => (
                        <option key={`target-user-${tu.id}`} value={tu.id}>
                          {tu.user_account.name}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                  戻る
                </Link>
                <button className="btn bg-gradient-dark btn-icon">
                  <i className="fas fa-check-circle"></i>変更を確定する
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
